import React, { useState, useEffect, useRef } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import SignaturePad from './SignaturePad';
import IncidentReportForm from './IncidentReportForm';
import EMRDashboard from './EMRDashboard';
import CaregiverSupplyPortal from './CaregiverSupplyPortal';
import offlineStorage from '../lib/offlineStorage';
import { 
  Clock, MapPin, CheckCircle, AlertTriangle, FileText,
  Play, Square, Car, ClipboardList, PenTool, Send,
  Loader2, Navigation, CheckSquare, ChevronDown, ChevronUp,
  Stethoscope, WifiOff, Wifi, Heart, Activity, Package, ArrowLeft
} from 'lucide-react';

// ADL Task Categories
const ADL_CATEGORIES = {
  'Personal Care': ['bathing', 'grooming', 'dressing', 'toileting', 'incontinence', 'oral_care'],
  'Nutrition': ['meal_prep', 'feeding'],
  'Mobility': ['mobility', 'transfer', 'ambulation', 'exercise'],
  'Health': ['medication', 'vital_signs'],
  'Household': ['light_housekeeping', 'laundry', 'errands'],
  'Support': ['companionship', 'supervision']
};

export default function ShiftToolkit() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [shifts, setShifts] = useState([]);
  const [selectedShift, setSelectedShift] = useState(null);
  const [adlTasks, setAdlTasks] = useState([]);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [mileage, setMileage] = useState('');
  const [progressNotes, setProgressNotes] = useState('');
  const [signature, setSignature] = useState(null);
  const [gpsLocation, setGpsLocation] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsError, setGpsError] = useState(null);
  const [clockingIn, setClockingIn] = useState(false);
  const [clockingOut, setClockingOut] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(['Personal Care', 'Nutrition']);
  const [showIncidentForm, setShowIncidentForm] = useState(false);
  const [showEMR, setShowEMR] = useState(false);
  const [showSupplyPortal, setShowSupplyPortal] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingSyncCount, setPendingSyncCount] = useState(0);
  const [carePlanInterventions, setCarePlanInterventions] = useState([]);
  const [showCarePlanSummary, setShowCarePlanSummary] = useState(true);

  // Initialize offline storage and listen for online/offline events
  useEffect(() => {
    offlineStorage.init().then(() => {
      updatePendingSyncCount();
    });

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const updatePendingSyncCount = async () => {
    try {
      const counts = await offlineStorage.getPendingSyncCount();
      setPendingSyncCount(counts.total);
    } catch (error) {
      console.error('Failed to get pending sync count:', error);
    }
  };

  useEffect(() => {
    fetchShifts();
    fetchAdlTasks();
  }, []);

  const fetchShifts = async () => {
    try {
      const response = await api.get('/shift-toolkit/employee-shifts');
      setShifts(response.data);
      
      // Auto-select today's in-progress shift
      const today = new Date().toDateString();
      const activeShift = response.data.find(s => 
        s.status === 'in_progress' || 
        (s.status === 'published' && new Date(s.shift_date).toDateString() === today)
      );
      if (activeShift) {
        setSelectedShift(activeShift);
        if (activeShift.adl_tasks) {
          setSelectedTasks(activeShift.adl_tasks);
        }
      }
    } catch (error) {
      console.error('Failed to fetch shifts:', error);
      toast.error('Failed to load shifts');
    } finally {
      setLoading(false);
    }
  };

  const fetchAdlTasks = async () => {
    try {
      const response = await api.get('/shift-toolkit/adl-tasks');
      setAdlTasks(response.data);
    } catch (error) {
      console.error('Failed to fetch ADL tasks:', error);
    }
  };

  // Fetch care plan interventions when shift is selected
  const fetchCarePlanInterventions = async (clientId) => {
    if (!clientId) {
      setCarePlanInterventions([]);
      return;
    }
    try {
      const response = await api.get(`/emr/care-plan/client/${clientId}/interventions`);
      setCarePlanInterventions(response.data.interventions || []);
    } catch (error) {
      console.error('Failed to fetch care plan interventions:', error);
      setCarePlanInterventions([]);
    }
  };

  // Fetch interventions when shift changes
  useEffect(() => {
    if (selectedShift?.client_id) {
      fetchCarePlanInterventions(selectedShift.client_id);
    } else {
      setCarePlanInterventions([]);
    }
  }, [selectedShift?.client_id]);

  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser'));
        return;
      }

      setGpsLoading(true);
      setGpsError(null);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          };
          setGpsLocation(coords);
          setGpsLoading(false);
          resolve(coords);
        },
        (error) => {
          setGpsLoading(false);
          let message = 'Unable to get your location';
          if (error.code === 1) message = 'Location access denied. Please enable GPS.';
          if (error.code === 2) message = 'Location unavailable. Please try again.';
          if (error.code === 3) message = 'Location request timed out. Please try again.';
          setGpsError(message);
          reject(new Error(message));
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    });
  };

  const handleClockIn = async () => {
    if (!selectedShift) {
      toast.error('Please select a shift first');
      return;
    }

    setClockingIn(true);
    try {
      const coords = await getCurrentLocation();
      
      const clockInData = {
        shift_id: selectedShift.id,
        latitude: coords.latitude,
        longitude: coords.longitude
      };

      if (!isOnline) {
        // Save to offline queue
        await offlineStorage.queueClockIn({
          shiftId: selectedShift.id,
          ...clockInData,
          offlineTimestamp: new Date().toISOString()
        });
        
        // Update local shift state
        setSelectedShift({
          ...selectedShift,
          status: 'in_progress',
          clock_in_time: new Date().toISOString(),
          offlineClockIn: true
        });
        
        // Save shift data for offline access
        await offlineStorage.saveShiftData({
          ...selectedShift,
          status: 'in_progress',
          clock_in_time: new Date().toISOString()
        });
        
        toast.success('Clocked in (offline mode - will sync when online)');
        await updatePendingSyncCount();
      } else {
        const response = await api.post('/shift-toolkit/clock-in', clockInData);
        toast.success('Clocked in successfully!');
        fetchShifts();
      }
    } catch (error) {
      const message = error.response?.data?.detail || error.message || 'Failed to clock in';
      toast.error(message);
    } finally {
      setClockingIn(false);
    }
  };

  const handleClockOut = async () => {
    if (!selectedShift) {
      toast.error('Please select a shift first');
      return;
    }

    if (selectedTasks.length === 0) {
      toast.error('Please complete at least one ADL task');
      return;
    }

    if (!signature) {
      toast.error('Client signature is required');
      return;
    }

    setClockingOut(true);
    try {
      const coords = await getCurrentLocation();
      
      const clockOutData = {
        shift_id: selectedShift.id,
        latitude: coords.latitude,
        longitude: coords.longitude,
        adl_tasks: selectedTasks,
        mileage: mileage ? parseFloat(mileage) : null,
        progress_notes: progressNotes || null,
        client_signature: signature
      };

      if (!isOnline) {
        // Save to offline queue
        await offlineStorage.queueClockOut({
          shiftId: selectedShift.id,
          ...clockOutData,
          offlineTimestamp: new Date().toISOString()
        });
        
        // Clear local state
        setSelectedTasks([]);
        setMileage('');
        setProgressNotes('');
        setSignature(null);
        setSelectedShift({
          ...selectedShift,
          status: 'pending_review',
          clock_out_time: new Date().toISOString(),
          offlineClockOut: true
        });
        
        toast.success('Shift saved (offline mode - will sync when online)');
        await updatePendingSyncCount();
      } else {
        const response = await api.post('/shift-toolkit/clock-out', clockOutData);
        toast.success('Shift submitted for approval!');
        setSelectedTasks([]);
        setMileage('');
        setProgressNotes('');
        setSignature(null);
        fetchShifts();
      }
    } catch (error) {
      const message = error.response?.data?.detail || error.message || 'Failed to submit shift';
      toast.error(message);
    } finally {
      setClockingOut(false);
    }
  };

  const toggleTask = (taskId) => {
    setSelectedTasks(prev => 
      prev.includes(taskId) 
        ? prev.filter(t => t !== taskId)
        : [...prev, taskId]
    );
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const getTaskById = (taskId) => {
    return adlTasks.find(t => t.id === taskId);
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '--:--';
    return new Date(dateStr).toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  // Show incident report form if active
  if (showIncidentForm && selectedShift?.client_id) {
    return (
      <IncidentReportForm
        clientId={selectedShift.client_id}
        employeeId={user?.id}
        onComplete={() => {
          setShowIncidentForm(false);
          toast.success('Incident report submitted!');
        }}
        onCancel={() => setShowIncidentForm(false)}
      />
    );
  }

  // Show EMR Dashboard if active - FULL SCREEN
  if (showEMR && selectedShift?.client_id) {
    return (
      <div className="min-h-screen bg-gradient-subtle -m-6 sm:-m-10">
        {/* EMR Full Screen Header */}
        <div className="nav-premium sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button
                  onClick={() => setShowEMR(false)}
                  variant="ghost"
                  className="text-purple-600 hover:bg-purple-50"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Toolkit
                </Button>
                <div className="border-l border-purple-200 pl-4">
                  <h1 className="text-lg font-bold text-purple-900">
                    {selectedShift.client_name || 'Patient'} - EMR
                  </h1>
                  <p className="text-xs text-gray-500">Electronic Medical Record</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Clock className="w-4 h-4" />
                Shift: {selectedShift.title}
              </div>
            </div>
          </div>
        </div>

        {/* Full Screen EMR Content */}
        <div className="max-w-7xl mx-auto px-6 py-6">
          <EMRDashboard 
            clientId={selectedShift.client_id}
            shiftId={selectedShift.id}
          />
        </div>
      </div>
    );
  }

  // Show Supply Portal if active
  if (showSupplyPortal) {
    return (
      <div className="space-y-4">
        <Button
          onClick={() => setShowSupplyPortal(false)}
          variant="ghost"
          className="text-purple-600 hover:bg-purple-50"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Shift Toolkit
        </Button>
        <CaregiverSupplyPortal 
          shiftId={selectedShift?.id}
          clientId={selectedShift?.client_id}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Offline Status Banner */}
      {!isOnline && (
        <div className="bg-amber-100 border border-amber-300 rounded-lg p-3 flex items-center gap-3" data-testid="offline-banner">
          <WifiOff className="w-5 h-5 text-amber-600" />
          <div className="flex-1">
            <p className="font-medium text-amber-800">You are offline</p>
            <p className="text-sm text-amber-700">Changes will be saved locally and synced when you're back online</p>
          </div>
          {pendingSyncCount > 0 && (
            <span className="bg-amber-500 text-white text-sm px-2 py-1 rounded-full">
              {pendingSyncCount} pending
            </span>
          )}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-purple-100 rounded-xl">
            <ClipboardList className="w-8 h-8 text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-purple-900">Shift Toolkit</h1>
            <p className="text-gray-600">Clock in, document tasks, and submit your shift</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Online Status Indicator */}
          <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
            isOnline ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}>
            {isOnline ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
            {isOnline ? 'Online' : 'Offline'}
          </div>
          {/* Supply Portal Button - Always visible */}
          <Button
            onClick={() => setShowSupplyPortal(true)}
            variant="outline"
            className="border-purple-300 text-purple-600 hover:bg-purple-50"
            data-testid="open-supply-portal-btn"
          >
            <Package className="w-4 h-4 mr-2" />
            Supplies
          </Button>
          {selectedShift?.status === 'in_progress' && (
            <>
              <Button
                onClick={() => setShowEMR(true)}
                variant="outline"
                className="border-purple-300 text-purple-600 hover:bg-purple-50"
                data-testid="open-emr-btn"
              >
                <Stethoscope className="w-4 h-4 mr-2" />
                Open EMR
              </Button>
              <Button
                onClick={() => setShowIncidentForm(true)}
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-50"
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Report Incident
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Shift Selection */}
      {shifts.length === 0 ? (
        <Card className="p-8 text-center border-purple-100">
          <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No shifts assigned to you</p>
          <p className="text-sm text-gray-400 mt-1">Contact your supervisor for shift assignments</p>
        </Card>
      ) : (
        <>
          {/* Today's Shifts */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {shifts.filter(s => ['published', 'in_progress'].includes(s.status)).slice(0, 4).map(shift => (
              <Card
                key={shift.id}
                onClick={() => setSelectedShift(shift)}
                className={`p-4 cursor-pointer transition-all ${
                  selectedShift?.id === shift.id
                    ? 'border-2 border-purple-500 bg-purple-50'
                    : 'border-purple-100 hover:border-purple-300'
                }`}
                data-testid={`shift-card-${shift.id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-semibold text-purple-900">{shift.title}</h3>
                    {shift.client_name && (
                      <p className="text-sm text-gray-600">Client: {shift.client_name}</p>
                    )}
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                    shift.status === 'in_progress' 
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {shift.status === 'in_progress' ? 'In Progress' : 'Ready'}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {shift.start_time} - {shift.end_time}
                  </span>
                  <span>{formatDate(shift.shift_date)}</span>
                </div>
                {shift.location && (
                  <p className="text-sm text-gray-500 mt-2 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {shift.location}
                  </p>
                )}
              </Card>
            ))}
          </div>

          {/* Active Shift Panel */}
          {selectedShift && (
            <Card className="p-6 border-purple-200">
              {/* Clock In/Out Section */}
              <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-200">
                <div>
                  <h2 className="text-xl font-bold text-purple-900">{selectedShift.title}</h2>
                  {selectedShift.client_name && (
                    <p className="text-gray-600">Client: {selectedShift.client_name}</p>
                  )}
                </div>

                {/* GPS Status */}
                <div className="flex items-center gap-4">
                  {gpsLoading && (
                    <div className="flex items-center gap-2 text-blue-600">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">Getting location...</span>
                    </div>
                  )}
                  {gpsLocation && !gpsLoading && (
                    <div className="flex items-center gap-2 text-green-600">
                      <Navigation className="w-4 h-4" />
                      <span className="text-sm">GPS Active</span>
                    </div>
                  )}
                  {gpsError && (
                    <div className="flex items-center gap-2 text-red-600">
                      <AlertTriangle className="w-4 h-4" />
                      <span className="text-sm">{gpsError}</span>
                    </div>
                  )}

                  {/* Clock In Button */}
                  {!selectedShift.clock_in_time && (
                    <Button
                      onClick={handleClockIn}
                      disabled={clockingIn}
                      size="lg"
                      className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-lg"
                      data-testid="clock-in-btn"
                    >
                      {clockingIn ? (
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      ) : (
                        <Play className="w-5 h-5 mr-2" />
                      )}
                      Clock In
                    </Button>
                  )}

                  {/* Clock Out Button */}
                  {selectedShift.clock_in_time && !selectedShift.clock_out_time && (
                    <div className="text-right">
                      <p className="text-sm text-gray-500 mb-1">
                        Clocked in at {formatTime(selectedShift.clock_in_time)}
                      </p>
                      {selectedShift.gps_verified && (
                        <p className="text-xs text-green-600 flex items-center justify-end gap-1">
                          <CheckCircle className="w-3 h-3" />
                          GPS Verified
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Shift Documentation (only show after clock in) */}
              {selectedShift.clock_in_time && !selectedShift.clock_out_time && (
                <div className="space-y-6">
                  {/* Care Plan Summary Widget */}
                  {carePlanInterventions.length > 0 && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg overflow-hidden">
                      <button
                        onClick={() => setShowCarePlanSummary(!showCarePlanSummary)}
                        className="w-full px-4 py-3 flex items-center justify-between bg-purple-100 hover:bg-purple-150"
                      >
                        <div className="flex items-center gap-2">
                          <Heart className="w-5 h-5 text-purple-600" />
                          <span className="font-semibold text-purple-900">Care Plan Guide</span>
                          <span className="text-xs bg-purple-200 text-purple-800 px-2 py-0.5 rounded-full">
                            {carePlanInterventions.length} interventions
                          </span>
                        </div>
                        {showCarePlanSummary ? (
                          <ChevronUp className="w-5 h-5 text-purple-600" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-purple-600" />
                        )}
                      </button>
                      
                      {showCarePlanSummary && (
                        <div className="p-4 space-y-3">
                          <p className="text-xs text-purple-700 mb-2">
                            Follow these care plan interventions during your shift:
                          </p>
                          {/* Group by category */}
                          {Object.entries(
                            carePlanInterventions.reduce((acc, int) => {
                              const cat = int.category || 'General';
                              if (!acc[cat]) acc[cat] = [];
                              acc[cat].push(int);
                              return acc;
                            }, {})
                          ).map(([category, interventions]) => (
                            <div key={category} className="space-y-2">
                              <h4 className="text-sm font-medium text-purple-800 flex items-center gap-1">
                                <Activity className="w-4 h-4" />
                                {category}
                              </h4>
                              {interventions.map((int, idx) => (
                                <div key={idx} className="ml-5 p-2 bg-white rounded border border-purple-100">
                                  <p className="text-sm text-gray-800">{int.intervention}</p>
                                  <div className="flex gap-3 mt-1">
                                    {int.frequency && (
                                      <span className="text-xs text-purple-600">
                                        <strong>When:</strong> {int.frequency}
                                      </span>
                                    )}
                                    {int.instructions && (
                                      <span className="text-xs text-gray-500">
                                        <strong>How:</strong> {int.instructions}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* ADL Task Checklist */}
                  <div>
                    <h3 className="font-semibold text-purple-900 mb-3 flex items-center gap-2">
                      <CheckSquare className="w-5 h-5" />
                      Activities Completed
                    </h3>
                    <div className="space-y-3">
                      {Object.entries(ADL_CATEGORIES).map(([category, taskIds]) => {
                        const categoryTasks = adlTasks.filter(t => taskIds.includes(t.id));
                        const completedCount = categoryTasks.filter(t => selectedTasks.includes(t.id)).length;
                        const isExpanded = expandedCategories.includes(category);
                        
                        return (
                          <div key={category} className="border border-gray-200 rounded-lg overflow-hidden">
                            <button
                              onClick={() => toggleCategory(category)}
                              className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100"
                            >
                              <span className="font-medium text-gray-800">{category}</span>
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-500">
                                  {completedCount}/{categoryTasks.length}
                                </span>
                                {isExpanded ? (
                                  <ChevronUp className="w-4 h-4 text-gray-400" />
                                ) : (
                                  <ChevronDown className="w-4 h-4 text-gray-400" />
                                )}
                              </div>
                            </button>
                            {isExpanded && (
                              <div className="p-3 grid grid-cols-2 gap-2">
                                {categoryTasks.map(task => (
                                  <label
                                    key={task.id}
                                    className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                                      selectedTasks.includes(task.id)
                                        ? 'bg-purple-100 text-purple-900'
                                        : 'hover:bg-gray-100'
                                    }`}
                                  >
                                    <Checkbox
                                      checked={selectedTasks.includes(task.id)}
                                      onCheckedChange={() => toggleTask(task.id)}
                                    />
                                    <span className="text-sm">{task.name}</span>
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Mileage */}
                  <div>
                    <Label htmlFor="mileage" className="flex items-center gap-2 mb-2">
                      <Car className="w-4 h-4 text-purple-600" />
                      Shift Mileage
                    </Label>
                    <Input
                      id="mileage"
                      type="number"
                      step="0.1"
                      placeholder="Enter miles driven"
                      value={mileage}
                      onChange={(e) => setMileage(e.target.value)}
                      className="max-w-xs"
                      data-testid="mileage-input"
                    />
                  </div>

                  {/* Progress Notes */}
                  <div>
                    <Label htmlFor="notes" className="flex items-center gap-2 mb-2">
                      <FileText className="w-4 h-4 text-purple-600" />
                      Progress Notes
                    </Label>
                    <Textarea
                      id="notes"
                      placeholder="Document client's condition, activities, and any observations..."
                      value={progressNotes}
                      onChange={(e) => setProgressNotes(e.target.value)}
                      rows={4}
                      data-testid="progress-notes-input"
                    />
                  </div>

                  {/* Client Signature */}
                  <div>
                    <Label className="flex items-center gap-2 mb-2">
                      <PenTool className="w-4 h-4 text-purple-600" />
                      Client/Guardian Signature *
                    </Label>
                    <SignaturePad
                      onSignatureChange={(data) => setSignature(data)}
                      data-testid="signature-pad"
                    />
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-end pt-4 border-t border-gray-200">
                    <Button
                      onClick={handleClockOut}
                      disabled={clockingOut || selectedTasks.length === 0 || !signature}
                      size="lg"
                      className="bg-purple-600 hover:bg-purple-700 text-white px-8"
                      data-testid="submit-shift-btn"
                    >
                      {clockingOut ? (
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      ) : (
                        <Send className="w-5 h-5 mr-2" />
                      )}
                      Clock Out & Submit
                    </Button>
                  </div>
                </div>
              )}

              {/* Completed Shift Summary */}
              {selectedShift.clock_out_time && (
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-green-800 mb-2">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-semibold">Shift Submitted</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm text-green-700">
                    <div>
                      <p>Clock In: {formatTime(selectedShift.clock_in_time)}</p>
                      <p>Clock Out: {formatTime(selectedShift.clock_out_time)}</p>
                    </div>
                    <div>
                      <p>Tasks: {selectedShift.adl_tasks?.length || 0} completed</p>
                      <p>Mileage: {selectedShift.mileage || 0} miles</p>
                    </div>
                  </div>
                  <p className="text-xs text-green-600 mt-2">
                    Status: {selectedShift.status === 'pending_approval' ? 'Pending Approval' : selectedShift.status}
                  </p>
                </div>
              )}
            </Card>
          )}
        </>
      )}
    </div>
  );
}
